// Ported from microsites/womens-conference/src/components/WelcomeCurtain.tsx.
// Changes: it renders into the widget's body-level overlay host (inside the
// page it would sit under the site header); it flies into the site's login
// pill rather than the microsite's own avatar, since that pill's drawer now
// carries her places (kit/drawer.ts); next/image -> img; the event and the
// ministry mark arrive as props; `always` replays it for the time machine.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePortalContainer } from '@church/nextjs-ui/bottom-sheet';
import { art as artUrl } from '../lib/art';
import { Avatar } from './Avatar';
import { Sprig, Swash, Vine } from './Botanical';

/**
 * Once per device. Set to `false` to dial the animation in, which replays it on
 * every reload; leave it `true` anywhere real, or a returning attendee gets
 * greeted on every single visit, which is the nagging badge this replaced.
 */
const ONCE_PER_DEVICE = true;
/**
 * One key, holding the event it was seen for rather than a bare `1`.
 *
 * A plain flag would be permanent: the 2028 conference is a new MP event, and
 * every returning attendee's browser would still say "seen" from 2026, so nobody
 * would ever get greeted again. Storing the id means the next conference simply
 * does not match, everyone is welcomed once more, and there is no stale key left
 * behind to sweep up.
 */
const SEEN_KEY = 'wc-welcome-seen';

/**
 * The site's login pill: the widget's mount in the header's `.login-target`
 * item (widgets/profile/widget/login.tsx). The header renders that item more
 * than once (desktop, sticky, off-canvas), so the one on screen wins.
 */
function avatarTarget(): HTMLElement | null {
  const mounts = Array.from(
    document.querySelectorAll<HTMLElement>('[data-login-root], .login-target')
  ).filter((el) => !el.closest('#slide-out-widget-area, .slide-out-widget-area'));
  return (
    mounts.find((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && r.top < window.innerHeight / 2;
    }) ?? null
  );
}

const HOLD_MS = 2600;
/** The panel contracting from the whole viewport down to one card. */
const FOLD_MS = 560;
const FLIGHT_MS = 760;
/** A shorter hold when she comes back to a tab that was in the background. */
const RETURN_HOLD_MS = 1100;

/** The key art's own proportions, so the folded card is not a crop of it. */
const ART_W = 800;
const ART_H = 452;

/**
 * Lift whatever gives the avatar its stacking context above the curtain, so the
 * ticket slots in behind her photo instead of landing on top of it.
 *
 * Raising the avatar button itself does nothing: it lives inside the page header,
 * which has its own z-index and therefore its own stacking context, and a child
 * cannot escape that. So walk up to the first ancestor that actually has a
 * z-index and move that one, then put it back.
 *
 * Returns the undo, or null if nothing needed moving.
 */
function raiseAbove(el: HTMLElement, z: number): (() => void) | null {
  let node: HTMLElement | null = el;
  while (node && node !== document.body) {
    if (getComputedStyle(node).zIndex !== 'auto') {
      const moved = node;
      const previous = moved.style.zIndex;
      moved.style.zIndex = String(z);
      return () => {
        moved.style.zIndex = previous;
      };
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * The welcome curtain: the whole page for about three seconds, then folded away.
 *
 * It replaces a badge that sat in the hero forever stating something she already
 * knew. This greets her on the conference's own watercolor, and then the panel
 * itself is what leaves: it contracts from full-bleed down to a single card
 * carrying the event painting, and that card files itself into the header
 * avatar. The site is revealed around the card as it shrinks, rather than behind
 * a fade, because the panel is the object in motion and not a veil over one.
 *
 * The point of the flight is the sheet behind that avatar. It holds her
 * breakouts, her lunch and her ticket, and nothing else on the page says so.
 *
 * Details that are load-bearing rather than decorative:
 *
 * - **The root is transparent; the panel carries the ground.** That is what lets
 *   the real page appear around the card mid-fold. An opaque root would keep the
 *   page hidden until the very end and leave a dead green field while it folded.
 * - **The fold animates top/right/bottom/left, not `scale`.** A viewport is not
 *   the painting's aspect ratio, so scaling to a card would squash the art. This
 *   costs layout on one element for half a second and is worth it.
 * - **Nothing renders on the server.** A takeover in the HTML would flash for
 *   every visitor, including the ones who should never see it.
 * - **It never starts leaving in a hidden tab.** A backgrounded tab freezes the
 *   document animation timeline at 0, so anything begun there never advances and
 *   never resolves, and the card would hang mid-page forever. Cmd-clicking a
 *   link is enough to hit that.
 * - **Teardown is on a timer, not on the animations.** `finished` is the happy
 *   path; if the tab is backgrounded mid-exit it never settles, and timers are
 *   the only thing that still fire.
 * - **It is dismissible.** Click, tap, or any key.
 * - **Reduced motion gets no fold and no flight**, just a fade.
 */
export function WelcomeCurtain({
  name,
  firstName,
  imageUrl,
  eventId,
  ministryMark,
  always = false,
  target: targetOf,
  returning = false,
}: {
  name: string;
  firstName?: string | null;
  imageUrl: string | null;
  eventId: number;
  ministryMark: string;
  /** Ignore the once-per-device memory (the time machine's preview). */
  always?: boolean;
  /** Where the ticket lands: the floating ticket. Falls back to the site's login pill. */
  target?: () => HTMLElement | null;
  /** After the weekend the greeting thanks her instead of confirming a seat. */
  returning?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  /** The green panel. This is the thing that folds and then travels. */
  const panel = useRef<HTMLDivElement>(null);
  /** Blooms, stems and sprigs: they go before the panel becomes a card. */
  const scenery = useRef<HTMLDivElement>(null);
  /** Portrait and greeting. */
  const column = useRef<HTMLDivElement>(null);
  /** The event painting, which is the card's face once folded. */
  const art = useRef<HTMLDivElement>(null);
  const leaving = useRef(false);
  const portal = usePortalContainer();
  const SEEN_VALUE = String(eventId);

  useEffect(() => {
    let seen = false;
    if (ONCE_PER_DEVICE && !always) {
      try {
        seen = localStorage.getItem(SEEN_KEY) === SEEN_VALUE;
      } catch {
        // Blocked storage: greet her every time rather than not at all.
      }
    }
    if (seen) return;

    setVisible(true);

    // A takeover that lets the page scroll underneath reads as a mistake.
    const html = document.documentElement;
    const wasOverflow = html.style.overflow;
    html.style.overflow = 'hidden';

    let timer = 0;
    let stopped = false;

    const arm = (delay: number) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (stopped || document.hidden) return; // visibilitychange re-arms
        leave();
      }, delay);
    };
    const onVisibility = () => {
      if (!document.hidden) arm(RETURN_HOLD_MS);
    };
    const onKey = () => leave();

    arm(HOLD_MS);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('keydown', onKey);

    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('keydown', onKey);
      html.style.overflow = wasOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function done() {
    // Hide the real node before asking React to drop it. `setVisible(false)`
    // goes through the concurrent scheduler, which a backgrounded tab defers:
    // the teardown timer fires, this runs, and the unmount render can still be
    // queued. Without this the card stays parked on screen at z-index 90.
    if (root.current) root.current.style.display = 'none';

    if (ONCE_PER_DEVICE) {
      try {
        localStorage.setItem(SEEN_KEY, SEEN_VALUE);
      } catch {
        // Nothing to do; she simply gets greeted again.
      }
    }
    document.documentElement.style.overflow = '';
    setVisible(false);
  }

  function leave() {
    if (leaving.current) return;
    leaving.current = true;

    const panelEl = panel.current;
    const target = targetOf?.() ?? avatarTarget();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const fade = (el: HTMLElement | null, ms: number, delay = 0) =>
      el?.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: ms,
        delay,
        easing: 'ease-out',
        fill: 'forwards',
      });

    if (reduced || !panelEl || !target) {
      fade(panelEl, 320);
      window.setTimeout(done, 380);
      return;
    }

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // The card it folds down to: the painting's aspect, comfortably inside the
    // viewport on a phone as well as a desktop.
    const cardW = Math.min(460, vw * 0.66);
    const cardH = (cardW * ART_H) / ART_W;
    const cardL = (vw - cardW) / 2;
    const cardT = (vh - cardH) / 2;

    // 1. The fold. The greeting goes first, then the weather, then the painting
    //    arrives as the panel closes in on its own proportions.
    fade(column.current, 240);
    fade(scenery.current, 420, 60);
    art.current?.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 420,
      delay: 140,
      easing: 'ease-in',
      fill: 'forwards',
    });

    panelEl.animate(
      [
        {
          top: '0px',
          right: '0px',
          bottom: '0px',
          left: '0px',
          borderRadius: '0px',
          boxShadow: '0 0 0 rgb(34 40 25 / 0)',
          // A scalloped screen edge would read as a rendering fault, so the
          // teeth are not there until there is a card for them to belong to.
          '--tooth': '0px',
        } as Keyframe,
        {
          top: `${cardT}px`,
          right: `${vw - cardL - cardW}px`,
          bottom: `${vh - cardT - cardH}px`,
          left: `${cardL}px`,
          borderRadius: '18px',
          boxShadow: '0 25px 50px -12px rgb(34 40 25 / 0.55)',
          '--tooth': '9px',
        } as Keyframe,
      ],
      { duration: FOLD_MS, easing: 'cubic-bezier(0.5, 0.02, 0.24, 1)', fill: 'forwards' }
    );

    // 2. The flight, once there is a card to fly. Transform only, so it never
    //    fights the inset the fold left behind.
    let lowerAvatar: (() => void) | null = null;
    const liftOff = window.setTimeout(() => {
      // Above the curtain's own z-[90], so the ticket passes behind her photo.
      lowerAvatar = raiseAbove(target, 95);
      const to = target.getBoundingClientRect();
      const dx = to.left + to.width / 2 - (cardL + cardW / 2);
      const dy = to.top + to.height / 2 - (cardT + cardH / 2);
      // Toward the avatar's width, not to nothing: it is being filed, not
      // deleted, and at scale 0 that reads as a vanish.
      const end = Math.max(to.width / cardW, 0.08);

      panelEl.animate(
        [
          { transform: 'translate(0, 0) scale(1) rotate(0deg)', opacity: 1 },
          {
            // Across before up, with a little turn, so it travels like a card
            // being tucked away rather than sliding on rails.
            transform: `translate(${dx * 0.58}px, ${dy * 0.3}px) scale(${(1 + end) / 2}) rotate(-6deg)`,
            opacity: 1,
            offset: 0.55,
          },
          {
            transform: `translate(${dx}px, ${dy}px) scale(${end}) rotate(-13deg)`,
            opacity: 0.85,
          },
        ],
        { duration: FLIGHT_MS, easing: 'cubic-bezier(0.42, 0, 0.62, 0.18)', fill: 'forwards' }
      );
    }, FOLD_MS);

    const catchAt = window.setTimeout(
      () => {
        target.classList.add('wm-avatar-catch');
        window.setTimeout(() => target.classList.remove('wm-avatar-catch'), 700);
      },
      FOLD_MS + FLIGHT_MS - 190
    );

    window.setTimeout(
      () => {
        window.clearTimeout(liftOff);
        window.clearTimeout(catchAt);
        target.classList.remove('wm-avatar-catch');
        lowerAvatar?.();
        done();
      },
      FOLD_MS + FLIGHT_MS + 360
    );
  }

  if (!visible) return null;

  const curtain = (
    // Transparent on purpose: the panel below owns the ground, which is the
    // only way the real page can appear around the card as it folds.
    <div
      ref={root}
      className="fixed inset-0 z-[90]"
      role="dialog"
      aria-label="Welcome"
      onClick={leave}
    >
      {/*
        `@container` so the ticket's type scales with the card as it folds rather
        than staying hero-sized on a 460px object.

        The hairline border is masked along with everything else, so it traces
        the teeth. Without it the silhouette only reads where the page behind
        happens to be lighter than the card, which over the dark hero is nowhere,
        and the toothed edge is the entire reason for this shape.
      */}
      <div
        ref={panel}
        className="wm-ticket-edges border-wm-cream/25 bg-wm-sage-950 [container-type:size] absolute inset-0 overflow-hidden border"
      >
        {/* The conference's own watercolor ground, the same export the hero and
            the footer stand on. It carries transparency, hence bg-wm-sage-950. */}
        <img
          src={artUrl('/art/watercolor.png')}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
        />
        {/* Light enough to still see the paint. Any darker and the watercolor
            might as well not be there. */}
        <div aria-hidden className="bg-wm-sage-950/40 absolute inset-0" />

        <div ref={scenery} className="absolute inset-0">
          {/* Bloom weather: the key art blurred past being a flower, which is
              rule 1 of the design system. */}
          <div aria-hidden className="absolute -top-52 -left-56">
            <div className="wm-bloom wm-bloom-blush opacity-70" />
          </div>
          <div aria-hidden className="absolute -right-64 -bottom-64">
            <div className="wm-bloom wm-bloom-marigold opacity-75" />
          </div>
          <div aria-hidden className="absolute -top-44 -right-44">
            <div className="wm-bloom wm-bloom-plum opacity-50" />
          </div>
          {/* Behind her, so she is the light in the middle of the page. */}
          <div aria-hidden className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="wm-bloom wm-bloom-blush opacity-40" />
          </div>
          {/* Drawn stems climbing the margins, roses at the tips.

              Solid cream at element opacity, never `text-wm-cream/25`: these
              strokes cross themselves at every leaf joint, and an alpha stroke
              colour double-darkens where it overlaps. */}
          <Vine
            aria-hidden
            className="text-wm-cream absolute -top-6 -left-3 hidden h-[34rem] w-20 opacity-25 lg:block"
          />
          <Vine
            flip
            aria-hidden
            className="text-wm-cream absolute -right-3 -bottom-8 hidden h-[34rem] w-20 opacity-25 lg:block"
          />
          <Vine
            flip
            aria-hidden
            className="text-wm-cream absolute -top-16 left-[22%] hidden h-64 w-14 rotate-[14deg] opacity-[0.14] xl:block"
          />
          <Vine
            aria-hidden
            className="text-wm-cream absolute right-[24%] -bottom-20 hidden h-64 w-14 -rotate-[10deg] opacity-[0.14] xl:block"
          />
          <span
            aria-hidden
            className="text-wm-cream absolute top-24 right-[17%] hidden opacity-30 sm:block"
          >
            <Sprig kind="bloom" className="h-9 w-9" />
          </span>
          <span
            aria-hidden
            className="text-wm-cream absolute bottom-24 left-[15%] hidden opacity-25 sm:block"
          >
            <Sprig kind="bud" className="h-8 w-8" />
          </span>
          <span
            aria-hidden
            className="text-wm-cream absolute top-[30%] left-[8%] hidden opacity-25 lg:block"
          >
            <Sprig kind="berry" className="h-7 w-7" />
          </span>
          <span
            aria-hidden
            className="text-wm-cream absolute right-[9%] bottom-[32%] hidden opacity-25 lg:block"
          >
            <Sprig kind="leaf" className="h-7 w-7" />
          </span>
        </div>

        {/*
          The card's face, arriving as the panel folds. Invisible at full bleed,
          where none of it would make sense across a whole viewport.

          Modelled on the plain cloakroom stub: a double rule inset from the
          edge, one heavy word in the middle, a serial repeated up both sides,
          and an ornament above and below. Ours swaps the sans for the display
          face the speakers' names are set in, the serial for the year, and the
          stars for the botanical marks the rest of the page is drawn with. The
          ground stays the hero's watercolor, which the panel already carries, so
          nothing on this card duplicates anything on the page behind it.

          Type is sized in container units so it scales with the card as it folds
          instead of staying hero-sized on a 460px object.
        */}
        <div ref={art} aria-hidden className="absolute inset-0 opacity-0">
          <div className="border-wm-cream/35 absolute inset-[4%] rounded-[10px] border" />
          <div className="border-wm-cream/15 absolute inset-[6.5%] rounded-[7px] border" />

          <p className="wm-kicker text-wm-cream absolute top-1/2 left-[3%] -translate-y-1/2 rotate-180 text-[clamp(0.34rem,1.7cqw,0.6rem)] opacity-40 [writing-mode:vertical-rl]">
            2026
          </p>
          <p className="wm-kicker text-wm-cream absolute top-1/2 right-[3%] -translate-y-1/2 text-[clamp(0.34rem,1.7cqw,0.6rem)] opacity-40 [writing-mode:vertical-rl]">
            2026
          </p>

          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-wm-cream opacity-45">
              <Sprig kind="bloom" className="h-[3.6cqh] min-h-3 w-[3.6cqh] min-w-3" />
            </span>
            <p className="wm-display text-wm-cream text-[clamp(1.6rem,11cqh,3.4rem)] leading-none tracking-wide">
              Ticket
            </p>
            <span className="text-wm-cream opacity-45">
              <Sprig kind="bloom" className="h-[3.6cqh] min-h-3 w-[3.6cqh] min-w-3" />
            </span>
            <p className="wm-kicker text-wm-cream mt-[3cqh] text-[clamp(0.34rem,1.9cqw,0.62rem)] opacity-70">
              {name}
            </p>
          </div>
        </div>

        <div className="relative flex h-full items-center justify-center px-6 text-center">
          <div ref={column} className="flex flex-col items-center">
            <div className="wm-curtain-pop ring-wm-cream/40 shadow-wm-sage-950/50 rounded-full shadow-2xl ring-2">
              <Avatar name={name} imageSrc={imageUrl} size={112} />
            </div>

            <div className="wm-curtain-rise flex flex-col items-center">
              <p className="wm-script text-wm-blush mt-7 text-3xl leading-none">{ministryMark}</p>
              <h2 className="wm-display text-wm-cream mt-2 text-5xl sm:text-6xl">
                {returning
                  ? `Thanks for coming${firstName ? `, ${firstName}` : ''}`
                  : `You're in${firstName ? `, ${firstName}` : ''}`}
              </h2>
              <Swash className="text-wm-cream mt-1 opacity-40" />
              <p className="text-wm-sage-200 mt-4 max-w-sm text-sm">
                Your breakouts, lunch and ticket are in the corner, any time.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  return portal ? createPortal(curtain, portal) : curtain;
}
